/*
 * Recognises Google Docs / Slides / Sheets / Forms / Drive links so the
 * embed UI can guide people. The trap this exists for: the owner pastes the
 * editing link (".../edit?usp=sharing"), sees it work because they are
 * signed in to Google, and assumes everyone else will too. Viewers get a
 * Google sign-in wall or a refused frame instead.
 *
 * Google serves the "/preview" form of a Docs, Slides or Sheets file inside a
 * frame to anyone the file is shared with, so that is the embed link we
 * recommend, together with setting the file's sharing to "Anyone with the
 * link" in Google.
 */

export type GoogleWorkspaceKind =
    | 'document'
    | 'presentation'
    | 'spreadsheets'
    | 'forms'
    | 'drive';

export interface GoogleWorkspaceLink {
    kind: GoogleWorkspaceKind;
    /** "Google Docs", "Google Slides", ... */
    label: string;
    /** The link as Google would open it (for an "Open in Google" link). */
    openUrl: string;
    /** True when the link is already a form Google will show in a frame. */
    isEmbedLink: boolean;
    /** The recommended embed link, when one can be derived. */
    embedUrl: string | null;
}

const labels: Record<GoogleWorkspaceKind, string> = {
    document: 'Google Docs',
    presentation: 'Google Slides',
    spreadsheets: 'Google Sheets',
    forms: 'Google Forms',
    drive: 'Google Drive'
};

const embedModes = ['preview', 'embed', 'pub', 'pubhtml', 'htmlview'];

export function parseGoogleWorkspaceLink(
    url: string | null | undefined
): GoogleWorkspaceLink | null {
    if (!url) {
        return null;
    }

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    const host = parsed.hostname.toLowerCase();
    const segments = parsed.pathname.split('/').filter(Boolean);

    if (host === 'docs.google.com') {
        const [product, d, ...rest] = segments;
        if (d !== 'd') {
            return null;
        }

        if (product === 'forms') {
            // Published forms look like /forms/d/e/{id}/viewform and embed
            // with ?embedded=true. The /forms/d/{id}/edit link uses a
            // different id, so there is nothing to derive from it.
            const isPublished = rest[0] === 'e';
            const embedded = parsed.searchParams.get('embedded') === 'true';
            const base = isPublished
                ? `https://docs.google.com/forms/d/e/${rest[1]}/viewform`
                : null;

            return {
                kind: 'forms',
                label: labels.forms,
                openUrl: url,
                isEmbedLink: isPublished && embedded,
                embedUrl: base ? `${base}?embedded=true` : null
            };
        }

        if (
            product === 'document' ||
            product === 'presentation' ||
            product === 'spreadsheets'
        ) {
            const [id, mode] = rest;
            if (!id) {
                return null;
            }

            return {
                kind: product,
                label: labels[product],
                openUrl: `https://docs.google.com/${product}/d/${id}/edit`,
                isEmbedLink: embedModes.includes(mode ?? ''),
                embedUrl: `https://docs.google.com/${product}/d/${id}/preview`
            };
        }

        return null;
    }

    if (host === 'drive.google.com') {
        let id: string | null = null;

        if (segments[0] === 'file' && segments[1] === 'd' && segments[2]) {
            id = segments[2];
        } else if (segments[0] === 'open') {
            id = parsed.searchParams.get('id');
        }

        if (!id) {
            return null;
        }

        return {
            kind: 'drive',
            label: labels.drive,
            openUrl: `https://drive.google.com/file/d/${id}/view`,
            isEmbedLink: segments[3] === 'preview',
            embedUrl: `https://drive.google.com/file/d/${id}/preview`
        };
    }

    return null;
}
